<template>
  <shortcut-overlay-shell v-if="isOpen" @close="shortcutHelp.close()">
    <div class="shortcut-help">
      <h2 class="shortcut-help__title">Keyboard shortcuts</h2>

      <div class="shortcut-help__groups">
        <div v-for="group in groups" :key="group.name" class="shortcut-help__group">
          <h3 class="shortcut-help__group-title">{{ group.name }}</h3>
          <div v-for="binding in group.bindings" :key="binding.combos.join()" class="shortcut-help__row">
            <span class="shortcut-help__description">{{ binding.description }}</span>
            <span class="shortcut-help__keys">
              <template v-for="(combo, index) in binding.combos" :key="combo">
                <span v-if="index > 0" class="shortcut-help__or">or</span>
                <kbd-combo :combo="combo" />
              </template>
            </span>
          </div>
        </div>
      </div>
    </div>
  </shortcut-overlay-shell>
</template>

<script setup lang="ts">
import KbdCombo from '@components/shortcuts/kbd-combo.vue';
import ShortcutOverlayShell from '@components/shortcuts/shortcut-overlay-shell.vue';
import { shortcutHelp } from '@lib/shortcut-help';
import { activeBindings, useShortcutLayer, type ActiveBinding } from '@lib/shortcuts';
import { computed, ref, watch } from 'vue';

const isOpen = shortcutHelp.isOpen;

// Snapshot the registry as of the moment the overlay opens: once our own layer
// is topmost it would shadow the context's Enter action in the live listing.
// This watch must be registered BEFORE useShortcutLayer below so it runs first.
const snapshot = ref<ActiveBinding[]>([]);
watch(isOpen, (open) => {
  if (open) snapshot.value = activeBindings.value;
});

const groups = computed(() => {
  const result: { name: string; bindings: ActiveBinding[] }[] = [];
  for (const binding of snapshot.value) {
    const group = result.find((g) => g.name === binding.scope);
    if (group) group.bindings.push(binding);
    else result.push({ name: binding.scope, bindings: [binding] });
  }
  // Global shortcuts first, then whatever the current page/overlays registered.
  return result.sort((a, b) => (a.name === 'Global' ? -1 : b.name === 'Global' ? 1 : 0));
});

const { register } = useShortcutLayer(isOpen, 'shortcut-help', { blockFallthrough: true });
register('Escape', () => shortcutHelp.close());
register('?', () => shortcutHelp.close());
</script>

<style scoped lang="scss">
@import '@styles/variables';
@import '@styles/text';

.shortcut-help {
  &__keys {
    display: inline-flex;
    align-items: center;
    gap: 0.6rem;
  }

  &__or {
    color: rgba($c-dark, 0.5);
    font-size: 1.1rem;
  }

  display: flex;
  flex-direction: column;
  min-height: 0;
  padding: 2rem 2.4rem 2.4rem;

  &__title {
    @include popper-title;
    margin: 0 0 1.2rem;
  }

  &__groups {
    overflow-y: auto;
  }

  &__group-title {
    margin: 1.6rem 0 0.6rem;
    color: rgba($c-dark, 0.5);
    font-size: 1.1rem;
    font-weight: 400;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  &__row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1.6rem;
    padding: 0.5rem 0;
    color: $c-dark;
    font-size: 1.4rem;
  }

  &__description {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}
</style>
